'use client';

import { useEffect } from 'react';
import { pingPresence } from '@/lib/api';
import { getFingerprint } from '@/lib/fingerprint';

const PING_INTERVAL_MS = 30_000;

/**
 * Invisible client-only component that fires a presence ping every
 * ~30s while the tab is in the foreground. Powers the admin Overview's
 * live-visitor tile.
 *
 * Hidden tabs (Page Visibility API) skip the interval — counting tabs
 * the user can't see inflates the number for no useful signal. On
 * becoming visible again we send an immediate ping so the count
 * catches up without waiting up to 30s.
 */
export function PresenceHeartbeat() {
  useEffect(() => {
    const fp = getFingerprint();
    if (fp === 'ssr') return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const tick = () => {
      if (document.visibilityState === 'visible') {
        void pingPresence(fp);
      }
    };

    tick();
    timer = setInterval(tick, PING_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return null;
}
